import {
  FolderKanban,
  MoreHorizontal,
  PlusCircle,
  RefreshCcw,
  Rocket,
  Server
} from "lucide-react";

import { StatusBadge } from "@/components/ui/status-badge";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import Link from "next/link";
import { useCallback, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { cn, fetcherTyped, formatDistanceTime, HttpError, okstatus } from "@/lib/utils";
import useSWR from "swr";
import chance from "chance";
import React from "react";

import { AppShell } from "@/components/AppShell";
import { Host, ProviderType } from "@/types";
import { menu } from "@/components/dashboard/Sidebar";
import { ObjectLink } from "@/components/OptionalLink";
import { AppLogo } from "@/components/app/AppLogo";
import { DeleteHostDialog } from "@/components/host/DeleteHostDialog";
import logger from "@/lib/logger";
import { SubscribeDialog } from "@/components/SubscribeDialog";
import { CopyValue } from "@/components/CopyValue";
import { ListHostsResponseSchema } from "@/types/schemas";
import { groupByProject } from "@/pages/app";
import { formatMarkdown } from "@/components/formatMarkdown";
import { FeatureHeader } from "@/components/dashboard/FeatureHeader";
import { getProviderMeta } from "@/lib/provider/meta";
import { useLocalStorage } from "@/components/useLocalStorage";
import { ListCardsViewToggle } from "@/components/ListCardsViewToggle";

function HostHeader({
  open,
  collapsible,
  haveActiveHosts,
  haveManagedServices,
  handleAdd
}: {
  open?: boolean;
  collapsible?: boolean;
  haveActiveHosts?: boolean;
  haveManagedServices?: boolean;
  handleAdd: () => Promise<void>;
}) {
  const { data: providers } = useSWR<ProviderType[]>("/api/tenant/provider");
  return (
    <FeatureHeader
      key="hosts"
      icon={<Server />}
      title="Servers"
      subtitle="Connect your own server or provision a new one to host your apps and services."
      open={open}
      collapsible={collapsible}
      actions={[
        { label: "Add a new server", onClick: handleAdd, variant: "default" },
        {
          label: "Documentation",
          href: "https://docs.dollardeploy.com/getting-started",
          variant: "secondary"
        }
      ]}
      checklist={[
        {
          label: formatMarkdown("Add integrations [Go to settings](/settings/integrations)"),
          done: providers && providers.map(getProviderMeta).some(p => p?.compute)
        },
        {
          label: "Create and prepare new server or connect existing",
          done: haveActiveHosts
        },
        {
          label: formatMarkdown(
            "Add managed services (e.g. Postgres or Redis) [Learn more](https://docs.dollardeploy.com/managed-services/)"
          ),
          done: haveManagedServices
        }
      ]}
    >
      {formatMarkdown(`
To run your apps, you need to have a server ready. You can create one using our integrations or connect an existing one.

1. Add host
2. Enter details for the existing host ([Check here for supported providers](https://docs.dollardeploy.com/which-vps-providers-are-supported-in-dollar-deploy/))
3. Or provision a new server using our integrations
4. Configure services needed for your apps (e.g. Postgres or Redis)
5. Prepare the server for deployment

`)}
    </FeatureHeader>
  );
}

const Page = () => {
  const router = useRouter();
  const tab = (router.query.tab as string) ?? "all";
  const {
    data: hosts,
    mutate,
    isValidating
  } = useSWR("/api/host", fetcherTyped(ListHostsResponseSchema));
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useLocalStorage<"list" | "cards">("host.view", "list");

  const handleCreateHost = useCallback(async () => {
    try {
      setLoading(true);
      await fetch("/api/host/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: chance().word({ length: 12 })
        })
      })
        .then(okstatus)
        .then(res => res.json() as Promise<Host>)
        .then(res => {
          router.push(`/host/${res.id}`);
        });
    } catch (err) {
      logger.warn("Failed to create host", err);
      if (err instanceof HttpError) {
        if (err.statusCode === 402) {
          setShowSubscribeDialog(true);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [router]);

  const filtered = useMemo(
    () =>
      hosts?.filter(h => {
        if (tab === "all") {
          return true;
        }
        // Draft hosts are stored with a null status
        if (tab === "draft") {
          return h.status === null;
        }
        return h.status === tab;
      }),
    [hosts, tab]
  );

  const { projects, groups } = useMemo(() => groupByProject(filtered), [filtered]);

  return (
    <AppShell menuItem={menu.host}>
      {showSubscribeDialog && (
        <SubscribeDialog onClose={() => setShowSubscribeDialog(false)} />
      )}
      <div
        className={cn(
          "mx-4 mt-4 mb-20 flex flex-col gap-4",
          hosts && hosts.length === 0 ? "items-center justify-center lg:m-16" : ""
        )}
      >
        <HostHeader
          open={hosts && hosts.length === 0}
          collapsible={hosts && hosts.length > 0}
          haveActiveHosts={hosts && hosts.some(h => h.status === "active")}
          haveManagedServices={
            hosts && hosts.some(h => h.services.filter(s => s.type !== "ddagent").length > 0)
          }
          handleAdd={handleCreateHost}
        />

        {hosts && hosts.length > 0 && (
          <Tabs value={tab} onValueChange={value => router.push("/host?tab=" + value)}>
            <div className="flex items-center justify-between">
              <TabsList className="hidden md:flex">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="draft">Draft</TabsTrigger>
                <TabsTrigger value="archived" className="hidden sm:flex">
                  Archived
                </TabsTrigger>
              </TabsList>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="h-8 gap-1 plausible-event-name=AddHost"
                  onClick={handleCreateHost}
                  disabled={loading}
                  aria-label="Add Host"
                >
                  <PlusCircle className="h-3.5 w-3.5 sm:mr-0.5" />
                  <span className="hidden sm:inline">Add Host</span>
                </Button>
                <Button size="sm" className="h-8 gap-1" onClick={() => router.push("/r")}>
                  <Rocket className="h-3.5 w-3.5 mr-0.5" /> Launch from template
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1"
                  aria-label="Refresh"
                  onClick={e => {
                    e.preventDefault();
                    mutate();
                  }}
                >
                  <RefreshCcw className="h-3.5 w-3.5 sm:hidden" />
                  <span className="hidden sm:inline">Refresh</span>
                </Button>
                <ListCardsViewToggle value={view} onValueChange={setView} />
              </div>
            </div>

            <TabsContent value={tab} className={cn(view === "cards" && "force-responsive")}>
              <Card>
                <CardContent>
                  <Table className="Responsive">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="md:pl-4">Name</TableHead>
                        <TableHead>IP address</TableHead>
                        <TableHead>Apps</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>
                          <span className="sr-only">Actions</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className={cn(isValidating && "opacity-50")}>
                      {Object.entries(groups).map(([projectId, hosts]) => (
                        <React.Fragment key={projectId}>
                          {projectId !== "0" || Object.keys(projects).length > 1 ? (
                            <TableRow className="transparent hover:transparent row-full">
                              <TableCell colSpan={6}>
                                <div className="flex items-center gap-2 font-semibold text-lg py-2">
                                  <FolderKanban className="w-5 h-5" />
                                  {projects[projectId]?.name || "No project assigned"}
                                </div>

                                {projects[projectId]?.description ? (
                                  <div className="prose dark:prose-invert prose-sm line-clamp-2">
                                    {formatMarkdown(projects[projectId]?.description)}
                                  </div>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          ) : null}

                          {hosts.map(host => (
                            <TableRow
                              key={host.id}
                              className="cursor-pointer hover:bg-muted/50 transition-colors"
                              onClick={() => router.push(`/host/${host.id}`)}
                            >
                              <TableCell className="pl-4 font-medium">
                                <ObjectLink
                                  href={`/host/${host.id}`}
                                  className="link"
                                  type="host"
                                  onClick={event => event.stopPropagation()}
                                >
                                  {host.name}
                                </ObjectLink>
                              </TableCell>
                              <TableCell data-label="IP address">
                                <CopyValue value={host.ipAddress || undefined} />
                              </TableCell>
                              <TableCell data-label="Apps" className="apps-stack">
                                <span className="inline-flex gap-1 flex-wrap">
                                  {host.apps.map(app => (
                                    <ObjectLink
                                      key={app.id}
                                      href={`/app/${app.id}`}
                                      className="link gap-1"
                                      onClick={event => event.stopPropagation()}
                                    >
                                      <AppLogo
                                        appType={app.type}
                                        templateId={app.templateId}
                                        className="h-4 w-4 shrink-0"
                                      />
                                      {app.title ?? app.name}
                                    </ObjectLink>
                                  ))}

                                  {host.apps.length === 0 && (
                                    <span className="text-muted-foreground">No apps</span>
                                  )}
                                </span>
                              </TableCell>
                              <TableCell data-label="Status">
                                <StatusBadge status={host.status} size="sm" fallback="draft" />
                              </TableCell>
                              <TableCell data-label="Created">
                                {formatDistanceTime(host.createdAt)}
                              </TableCell>
                              <TableCell className="items-center gap-2 flex">
                                <span
                                  className="inline-flex items-center gap-2"
                                  onClick={event => event.stopPropagation()}
                                  onPointerDown={event => event.stopPropagation()}
                                  onKeyDown={event => event.stopPropagation()}
                                >
                                  <Button variant="outline" size="sm" asChild>
                                    <Link href={`/host/${host.id}`}>Open</Link>
                                  </Button>

                                  <DropdownMenu modal={false}>
                                    <DropdownMenuTrigger asChild>
                                      <Button aria-haspopup="true" size="icon" variant="ghost">
                                        <MoreHorizontal className="h-4 w-4" />
                                        <span className="sr-only">Toggle menu</span>
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                      <DropdownMenuItem asChild>
                                        <Link href={`/host/${host.id}/edit`}>Edit</Link>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem asChild>
                                        <Link href={`/journal?hostId=${host.id}`}>Logs</Link>
                                      </DropdownMenuItem>
                                      <DeleteHostDialog
                                        id={host.id}
                                        onDelete={() => mutate()}
                                      />
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}

                      {filtered && filtered?.length === 0 && (
                        <TableRow className="row-full">
                          <TableCell colSpan={6} className="text-center text-muted-foreground">
                            No hosts found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
                <CardFooter className="px-4">
                  <div className="text-xs text-muted-foreground">
                    Showing{" "}
                    <strong>
                      {filtered && filtered?.length > 0 ? 1 : 0}-{filtered?.length}
                    </strong>{" "}
                    of <strong>{filtered?.length}</strong> hosts
                  </div>
                </CardFooter>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </AppShell>
  );
};

export default Page;
